// import type { Lead } from '@/models/lead';
import { Typography } from "@material-ui/core";
import { lazyWithRetry as lazy, toSingular } from "../../../utils/lazy";
// import { Loader, Text } from "@material-ui/core";
import { type IJoditEditorProps } from "jodit-react";
import _ from "lodash";
import { Suspense, useMemo, type ReactNode } from "react";
// import { useTranslation } from "react-i18next";

const JoditReactEditor = lazy(() => import("jodit-react"));

interface JoditEditorProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: ReactNode;
  withLeadProperty?: boolean;
  config?: IJoditEditorProps["config"];
  listInsertField?: Array<string>;
  mapFieldName?: Record<string, string>;
  tableType?: string;
}

function JoditEditor({
  value,
  onChange,
  placeholder,
  error,
  withLeadProperty = true,
  config,
  listInsertField,
  mapFieldName,
  tableType = "curriculum",
}: JoditEditorProps) {
  // const { t } = useTranslation();

  const joditConfig: IJoditEditorProps["config"] = useMemo(() => {
    const baseConfigs = {
      ...JODIT_CONFIG,
      ...config,
      uploader: {
        url: "",
      },
    };
    if (!withLeadProperty) return baseConfigs;
    return {
      ...baseConfigs,
      extraButtons: [
        {
          name: "Chèn dữ liệu",
          popup: (editor: any) => {
            if (listInsertField) {
              return generateJoditInsertPopup(editor, [
                ...listInsertField.map((property) => ({
                  label:
                    mapFieldName && mapFieldName[property]
                      ? mapFieldName[property]
                      : `${toSingular(tableType)}.${property}`,
                  pascalCase: _.upperFirst(property),
                })),
              ]);
            }
            return generateJoditInsertPopup(editor, [
              ...LIST_CR_PROPERTY.map((property) => ({
                label: `${toSingular(tableType)}.${property}`,
                pascalCase: _.upperFirst(property),
              })),
              // ...(leadCustomFields?.map((customField) => ({
              //   label: customField.name,
              //   pascalCase: customField.upperCamelCaseName
              // })) ?? [])
            ]);
          },
        },
      ],
      placeholder: placeholder || `Chèn dữ liệu bằng cách nhấn vào nút "Chèn dữ liệu"`,
    };
  }, [config, placeholder, withLeadProperty, listInsertField, tableType]);
  // }, [config, leadCustomFields, placeholder, t, withLeadProperty]);

  return (
    <Suspense fallback={<div>Loading Editor...</div>}>
      <JoditReactEditor config={joditConfig} value={value} onBlur={onChange} />
      {!!error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Suspense>
  );
}

const JODIT_CONFIG = {
  buttons: [
    "bold",
    "italic",
    "underline",
    "brush",
    "|",
    "strikethrough",
    "eraser",
    "|",
    "font",
    "fontsize",
    "paragraph",
    "lineHeight",
    "|",
    "superscript",
    "subscript",
    "|",
    "file",
    "image",
    "hr",
    "table",
    "link",
    "symbols",
    "|",
    "cut",
    "copy",
    "paste",
    "selectall",
    "|",
    "align",
    "ul",
    "ol",
    "indent",
    "outdent",
    "|",
    "undo",
    "redo",
    "find",
    "|",
  ],
  disablePlugins: ["video", "preview", "about", "class-span", "powered-by-jodit", "source", "print", "add-new-line"],
  events: {
    getIcon: (_name: string, _control: any, clearName: string) => {
      let code = clearName;
      if (!code || code === "empty") return null;
      switch (clearName) {
        case "bin":
          code = "trash";
          break;
        case "pencil":
          code = "edit";
          break;
        case "valign":
          code = "arrows-alt-v";
          break;
        case "ok":
          code = "check";
          break;
        case "Image":
          code = "image";
          break;
        case "Advanced":
          code = "cog";
          break;
        case "brush":
          code = "paint-brush";
          break;
        case "left":
        case "right":
        case "center":
        case "justify":
          code = `align-${clearName}`;
          break;
        case "fontsize":
          code = "text-height";
          break;
        case "paragraph":
          code = "heading";
          break;
        case "lineHeight":
          code = "arrows-alt-v";
          break;
        case "image":
          code = "images";
          break;
        case "hr":
          code = "minus";
          break;
        case "symbols":
          code = "percent";
          break;
        case "select_all":
          code = "hand-paper";
          break;
        case "ul":
          code = "list-ul";
          break;
        case "ol":
          code = "list-ol";
          break;
        default:
          break;
      }
      return `<i style="font-size:14px" class="fa fa-${code} fa-xs"></i>`;
    },
  },
  enter: "div",
  minHeight: 400,
  // maxHeight: 550,
  uploader: {
    insertImageAsBase64URI: true,
  },
  toolbarAdaptive: false,
  toolbarSticky: false,
  showTooltipDelay: 0,
  showXPathInStatusbar: false,
} as IJoditEditorProps["config"];

// const LIST_LEAD_PROPERTY: Array<keyof Lead> = [
const LIST_LEAD_PROPERTY: Array<any> = [
  "code",
  "name",
  "phone",
  "secondPhone",
  "email",
  "dob",
  "identityCard",
  "socialName",
  "fbLink",
  "registeredAt",
  "description",
  "qualityAssessment",
];

const LIST_CR_PROPERTY: Array<any> = [
  "name",
  "year",
  "departmentId",
  "registrationDate",
  "isFirstCompile",
  "languageCompile",
  "courseId",
  "statusId",
  "curriculumInfo",
];

const generateJoditInsertPopup = (editor: any, listInsertBtn: Array<{ label: string; pascalCase: string }>) => {
  const container = editor.create.div();
  container.style.margin = "-8px";
  listInsertBtn.forEach(({ label, pascalCase }) => {
    const button: HTMLButtonElement = editor.create.fromHTML(/* html */ `
    <button class="jodit-custom-btn">
      <span style="max-width: 120px">${label}</span>
      <span style="font-family: RobotoBold">- ${pascalCase}</span>
    </button>
  `);
    button.addEventListener("click", () => {
      editor.selection.insertHTML(`{{${pascalCase}}}`);
    });

    container.appendChild(button);
  });

  return container;
};

export default JoditEditor;

